import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingExperimentEntity,
    marketingTierSlotConfigEntity,
    marketingSprintEntity,
} from 'mj_generatedentities';
import { runAction, parseActionMessageJson } from '../../shared/run-action.util';

type ExperimentRow = Pick<
    marketingExperimentEntity,
    'ID' | 'Code' | 'Name' | 'Hypothesis' | 'Tactic' | 'ExperimentStatus' | 'ExperimentTier' | 'ExperimentTierID'
    | 'Channel' | 'Budget' | 'StartDate' | 'DecisionDate' | 'ProposalConfidence' | 'RequiresOverride'
>;

interface SprintCapacityTier {
    tier?: string;
    name?: string;
    free?: number;
    available?: number;
    total?: number;
    capacity?: number;
    used?: number;
}

interface SprintCapacityResp {
    success?: boolean;
    sprintLabel?: string;
    sprintNumber?: number;
    tiers?: SprintCapacityTier[];
    wipCap?: number;
    wipUsed?: number;
    wipFree?: number;
}

function isSprintCapacityResp(v: unknown): v is SprintCapacityResp {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    return r['tiers'] === undefined || Array.isArray(r['tiers']);
}

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-backlog',
    template: `
<mkt-ws-page-head
    eyebrow="Experiments · Backlog"
    title="Agent-proposed experiment backlog"
    sub="Every item below was proposed after checking Learnings for anti-patterns. Tier 1 launches first; Tier 2 fills strategic gaps; Tier 3 explores new channels."
    [hasActions]="true">
    <div actions>
        <button class="bk__btn" type="button" (click)="navigate('experiments')"><i class="fa-solid fa-arrow-left"></i> Back</button>
        <button class="bk__btn" type="button" (click)="navigate('experiments/propose')"><i class="fa-solid fa-plus"></i> Propose yours</button>
    </div>
</mkt-ws-page-head>

<div *ngIf="loading" class="bk__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading backlog…</div>

<ng-container *ngIf="!loading">
    <!-- Sprint capacity strip -->
    <mkt-ws-card class="bk__sec">
        <div class="bk__cap">
            <span class="bk__caps-ink">{{ capacityLabel }} capacity</span>
            <span class="bk__cap-item" *ngFor="let t of capacityTiers">
                <span class="bk__cap-label">{{ t.label }}</span>
                <span class="bk__mono">{{ t.free }} / {{ t.total }} free</span>
            </span>
            <span class="bk__cap-wip" *ngIf="wipCap != null">
                <i class="fa-solid fa-shield-halved"></i>
                WIP cap: {{ wipCap }} active / sprint
            </span>
        </div>
    </mkt-ws-card>

    <div class="bk__totals">
        <mkt-ws-card>
            <div class="bk__caps">Tier 1 · Exploit</div>
            <div class="bk__big">{{ tiered.t1.length }}</div>
            <div class="bk__sub">Known channel · $0 cost · high confidence</div>
        </mkt-ws-card>
        <mkt-ws-card>
            <div class="bk__caps">Tier 2 · Fill gaps</div>
            <div class="bk__big">{{ tiered.t2.length }}</div>
            <div class="bk__sub">Strategic priority · meaningful budget</div>
        </mkt-ws-card>
        <mkt-ws-card>
            <div class="bk__caps">Tier 3 · Explore</div>
            <div class="bk__big">{{ tiered.t3.length }}</div>
            <div class="bk__sub">New channels · small bets</div>
        </mkt-ws-card>
    </div>

    <ng-container *ngFor="let group of [
        { key: 't1', label: 'Tier 1 · Exploit proven winners', meta: '$0 cost · launch first' },
        { key: 't2', label: 'Tier 2 · Fill strategic gaps', meta: 'High learning value · meaningful budget' },
        { key: 't3', label: 'Tier 3 · Explore new channels', meta: 'Small bets · unknown ROI' }
    ]">
        <div class="bk__group" *ngIf="rowsFor(group.key).length > 0">
            <div class="bk__group-head">
                <span class="bk__caps-ink">{{ group.label }}</span>
                <div class="bk__hr"></div>
                <span class="bk__sub">{{ group.meta }}</span>
            </div>
            <mkt-ws-card [noPadding]="true">
                <table class="bk__table">
                    <thead>
                        <tr>
                            <th class="bk__t-id">ID</th>
                            <th>Experiment</th>
                            <th>Hypothesis</th>
                            <th class="bk__t-channel">Channel</th>
                            <th class="bk__t-num">{{ group.key === 't1' ? 'Confidence' : 'Budget' }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of rowsFor(group.key)" (click)="openDetail(row.ID)">
                            <td class="bk__t-id bk__mono">{{ row.Code || row.ID.slice(0,8) }}</td>
                            <td class="bk__t-name">
                                <div class="bk__t-name-line">
                                    <span>{{ row.Name || '—' }}</span>
                                    <mkt-ws-pill *ngIf="row.RequiresOverride" variant="warn" [title]="overrideTitle(row)">
                                        Held for Review · over tier cap
                                    </mkt-ws-pill>
                                </div>
                                <div class="bk__sub bk__t-rationale">{{ tierRationale(row) }}</div>
                            </td>
                            <td class="bk__t-hypo">{{ row.Hypothesis || '—' }}</td>
                            <td><mkt-ws-pill variant="muted">{{ row.Channel || '—' }}</mkt-ws-pill></td>
                            <td class="bk__t-num bk__mono">
                                <ng-container *ngIf="group.key === 't1'; else budgetCell">{{ row.ProposalConfidence || '—' }}</ng-container>
                                <ng-template #budgetCell>{{ formatBudget(row.Budget) }}</ng-template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </mkt-ws-card>
        </div>
    </ng-container>

    <mkt-ws-card *ngIf="proposed.length === 0">
        <div class="bk__empty">No proposed experiments in the backlog. The agent's next proposal cycle will surface them.</div>
    </mkt-ws-card>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .bk__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .bk__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .bk__btn:hover { background: var(--mj-bg-page, #f5f5f5); }

        .bk__caps, .bk__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .bk__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .bk__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .bk__big { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 22px; color: var(--mj-text-primary, #1a1a1a); margin: 6px 0 4px; }
        .bk__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .bk__sec { margin-bottom: 16px; }
        .bk__cap { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; font-size: 12.5px; }
        .bk__cap-item { display: flex; align-items: center; gap: 8px; }
        .bk__cap-label { color: var(--mj-text-secondary, #71717a); }
        .bk__cap-wip { margin-left: auto; display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .bk__cap-wip i { color: var(--mj-color-success-700, #047857); }

        .bk__totals { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }

        .bk__group { margin-bottom: 24px; }
        .bk__group-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
        .bk__hr { flex: 1; height: 1px; background: var(--mj-border-default, #e0e0e0); }

        .bk__table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .bk__table thead th { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); padding: 10px 16px; text-align: left; background: var(--mj-bg-page, #f5f5f5); }
        .bk__table th.bk__t-id { width: 90px; }
        .bk__table th.bk__t-channel { width: 110px; }
        .bk__table th.bk__t-num { width: 130px; text-align: right; }
        .bk__table tbody tr { border-top: 1px solid var(--mj-border-default, #e0e0e0); cursor: pointer; transition: background 0.15s; }
        .bk__table tbody tr:hover { background: var(--mj-bg-page, #f5f5f5); }
        .bk__table td { padding: 10px 16px; vertical-align: top; }
        .bk__table td.bk__t-id { color: var(--mj-text-secondary, #71717a); }
        .bk__table td.bk__t-num { text-align: right; }
        .bk__t-name { font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .bk__t-name-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .bk__t-rationale { margin-top: 4px; font-weight: 400; }
        .bk__t-hypo { color: var(--mj-text-secondary, #71717a); }

        .bk__empty { padding: 32px 0; text-align: center; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }

        @media (max-width: 1024px) {
            .bk__totals { grid-template-columns: 1fr; }
        }
    `]
})
export class ExperimentsBacklogSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.reset();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    proposed: ExperimentRow[] = [];
    tierConfigs: marketingTierSlotConfigEntity[] = [];

    capacityLabel = 'Sprint';
    capacityTiers: { label: string; free: number; total: number }[] = [];
    wipCap: number | null = null;

    rowsFor(key: string): ExperimentRow[] {
        return this.tiered[key as 't1' | 't2' | 't3'] || [];
    }

    get tiered(): { t1: ExperimentRow[]; t2: ExperimentRow[]; t3: ExperimentRow[] } {
        const out = { t1: [] as ExperimentRow[], t2: [] as ExperimentRow[], t3: [] as ExperimentRow[] };
        for (const r of this.proposed) {
            const tier = (r.ExperimentTier || '').toLowerCase();
            if (tier.includes('1') || tier.includes('exploit')) out.t1.push(r);
            else if (tier.includes('2') || tier.includes('fill')) out.t2.push(r);
            else out.t3.push(r);
        }
        return out;
    }

    formatBudget(b: number | null | undefined): string {
        if (b == null) return '—';
        if (b === 0) return '$0';
        return `$${Math.round(b).toLocaleString()}`;
    }

    /** Static tier rationale text — derived from tier + budget without round-tripping the LLM-backed Suggest action per row. */
    tierRationale(row: ExperimentRow): string {
        const tier = (row.ExperimentTier || '').toLowerCase();
        if (tier.includes('1') || tier.includes('exploit')) {
            return 'Proven channel · low cost · high confidence — exploit a known winner.';
        }
        if (tier.includes('2') || tier.includes('fill')) {
            return 'Strategic gap · meaningful budget — fills an ICP/channel hole the foundation flagged.';
        }
        return 'New channel or audience · small bet — explore with a capped budget.';
    }

    overrideTitle(row: ExperimentRow): string {
        const cap = this.tierBudgetCap(row.ExperimentTierID);
        if (cap == null) return 'Exceeds the tier-level budget cap';
        const budget = row.Budget ?? 0;
        return `Budget $${Math.round(budget).toLocaleString()} exceeds tier cap $${Math.round(cap).toLocaleString()}`;
    }

    private tierBudgetCap(tierId: string | null | undefined): number | null {
        if (!tierId) return null;
        const cfg = this.tierConfigs.find(c => c.ExperimentTierID === tierId);
        const cap = cfg?.BudgetCapUsd;
        return cap == null ? null : Number(cap);
    }

    openDetail(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [expRes, tierRes] = await rv.RunViews([
                {
                    EntityName: 'Experiments',
                    ExtraFilter: `CompanyID='${cid}' AND ExperimentStatus IS NOT NULL`,
                    // Sort by proposed_by source per #629 — agent-driven proposals
                    // surface above founder-requested ones; tier sequences within
                    // each source bucket; recency breaks final ties.
                    OrderBy: `CASE ProposedBy WHEN 'agent_strategy' THEN 1 WHEN 'agent_signal' THEN 2 WHEN 'user' THEN 3 ELSE 4 END ASC, ExperimentTier ASC, __mj_CreatedAt DESC`,
                    MaxRows: 200,
                    ResultType: 'simple',
                },
                {
                    EntityName: 'Tier Slot Configs',
                    ExtraFilter: `CompanyID='${cid}'`,
                    ResultType: 'simple',
                },
            ]);
            if (expRes.Success) {
                const all = (expRes.Results || []) as ExperimentRow[];
                this.proposed = all.filter(r => r.ExperimentStatus === 'Proposed');
            } else if (expRes.ErrorMessage) {
                LogError(`[ExperimentsBacklog] experiments query failed: ${expRes.ErrorMessage}`);
            }
            if (tierRes.Success) {
                this.tierConfigs = tierRes.Results as marketingTierSlotConfigEntity[];
            }

            // Capacity via action — needs an active sprint to scope against
            const sprintRes = await rv.RunView({
                EntityName: 'Sprints',
                ExtraFilter: `CompanyID='${cid}' AND Status IN ('Active','Planning')`,
                OrderBy: '__mj_CreatedAt DESC',
                MaxRows: 1,
                ResultType: 'simple',
            });
            const activeSprintId = sprintRes.Success && sprintRes.Results && sprintRes.Results.length > 0
                ? (sprintRes.Results[0] as { ID: string }).ID
                : null;
            const capRes = activeSprintId
                ? await runAction('Get Sprint Slot Capacity', [
                    { Name: 'CompanyID', Value: cid },
                    { Name: 'SprintID', Value: activeSprintId },
                ])
                : { success: false, message: 'No active sprint', resultCode: 'NO_SPRINT' };
            if (capRes.success) {
                const parsed = parseActionMessageJson(capRes.message, isSprintCapacityResp);
                if (parsed) {
                    this.capacityLabel = parsed.sprintLabel || (parsed.sprintNumber ? `Sprint ${parsed.sprintNumber}` : 'Sprint');
                    this.wipCap = parsed.wipCap ?? null;
                    this.capacityTiers = (parsed.tiers || []).map(t => ({
                        label: (t.tier || t.name || '').replace(/^Tier\s*/i, 'Tier ') || 'Tier',
                        free: t.free ?? t.available ?? 0,
                        total: t.total ?? t.capacity ?? 0,
                    }));
                } else {
                    this.deriveCapacityFromConfigs();
                }
            } else {
                this.deriveCapacityFromConfigs();
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsBacklog] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private deriveCapacityFromConfigs(): void {
        if (this.tierConfigs.length === 0) {
            this.capacityTiers = [];
            return;
        }
        this.capacityTiers = this.tierConfigs.map(c => ({
            label: c.ExperimentTier || 'Tier',
            free: c.SlotsPerSprint,
            total: c.SlotsPerSprint,
        }));
    }

    private reset(): void {
        this.proposed = [];
        this.capacityTiers = [];
        this.wipCap = null;
        this.cdr.detectChanges();
    }
}
